import React, { useState } from 'react'
import { useTheme } from '@/context/ThemeContext'

type Props = {
  label: string
  placeholder?: string
  value?: string | undefined
  onChange?: (value: string) => void
  type?: string
  readOnly?: boolean
  hint?: string | undefined
}

const Field: React.FC<Props> = ({ label, placeholder, value, onChange, type = 'text', readOnly = false, hint }) => {
  const { currentTheme: t } = useTheme()
  const [focused, setFocused] = useState(false)

  // Controlled when a parent passes onChange (needs the value to save); otherwise
  // uncontrolled (defaultValue) — preserves the existing placeholder-only inputs
  // (e.g. PasswordSection) that don't track state at all.
  const valueProps = onChange
    ? { value: value ?? '', onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value) }
    : { defaultValue: value }

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: t.mutedText }}>
        {label}
      </label>
      <input
        type={type}
        {...valueProps}
        placeholder={placeholder}
        readOnly={readOnly}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="w-full h-[42px] rounded-[10px] px-3.5 text-[13.5px] outline-none transition-[border,box-shadow] duration-150"
        style={{
          backgroundColor: readOnly ? t.hoverBackground : t.inputBackground,
          border: `1.5px solid ${focused && !readOnly ? t.accent : t.border}`,
          color: t.text,
          cursor: readOnly ? 'default' : 'text',
          boxShadow: focused && !readOnly ? `0 0 0 3px rgba(79,70,229,0.12)` : 'none',
        }}
      />
      {hint && <p className="text-[11.5px]" style={{ color: t.mutedText }}>{hint}</p>}
    </div>
  )
}

export default React.memo(Field)
